import { CheckIcon } from "@heroicons/react/20/solid";
import { useState, type ReactNode } from "react";
import { useProject } from "@/lib/project";
import { useEpics, useSprints } from "@/lib/queries";
import type { Color } from "@/lib/types";
import { cn } from "@/lib/utils";
import { StageBadge, Tag } from "../ui";
import { MenuLabel, Popover } from "../ui/Popover";

interface Choice {
  id: string | null;
  label: string;
  color?: Color;
  hint?: string;
}

/** Generic single-choice popover list with search. */
export function ChoicePopover({
  value,
  choices,
  onChange,
  trigger,
  disabled,
  title,
  badge = "tag",
  className,
}: {
  value: string | null;
  choices: Choice[];
  onChange: (id: string | null) => void;
  trigger: (t: { ref: React.RefObject<HTMLButtonElement | null>; onClick: (e: React.MouseEvent) => void; open: boolean }) => ReactNode;
  disabled?: boolean;
  title?: string;
  badge?: "tag" | "stage" | "plain";
  className?: string;
}) {
  const [q, setQ] = useState("");
  const filtered = choices.filter((c) => c.label.toLowerCase().includes(q.toLowerCase()));
  return (
    <Popover disabled={disabled} trigger={trigger} className={cn("w-64 p-1", className)} onOpenChange={(o) => !o && setQ("")}>
      {(close) => (
        <>
          {choices.length > 7 && <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="field mb-1" dir="auto" />}
          {title && <MenuLabel>{title}</MenuLabel>}
          {filtered.map((c) => (
            <button
              key={c.id ?? "none"}
              type="button"
              onClick={() => {
                onChange(c.id);
                close();
              }}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-sm hover:bg-hover"
            >
              <span className="min-w-0 flex-1 truncate">
                {badge === "stage" && c.color ? (
                  <StageBadge color={c.color}>{c.label}</StageBadge>
                ) : badge === "tag" && c.color ? (
                  <Tag color={c.color}>{c.label}</Tag>
                ) : (
                  <span dir="auto" className={cn(c.id === null && "text-muted")}>
                    {c.label}
                  </span>
                )}
              </span>
              {c.hint && <span className="text-xs text-faint">{c.hint}</span>}
              {value === c.id && <CheckIcon className="size-4 shrink-0 text-muted" />}
            </button>
          ))}
          {filtered.length === 0 && <div className="px-2 py-2 text-sm text-faint">No matches</div>}
        </>
      )}
    </Popover>
  );
}

export function StagePicker({
  value,
  onChange,
  trigger,
}: {
  value: string;
  onChange: (id: string) => void;
  trigger: Parameters<typeof ChoicePopover>[0]["trigger"];
}) {
  const { bundle, canEdit } = useProject();
  const hints: Record<string, string> = { in_progress: "starts timer", qa: "QA", done: "done" };
  return (
    <ChoicePopover
      value={value}
      disabled={!canEdit}
      badge="stage"
      title="Stage"
      choices={bundle.stages.map((s) => ({ id: s.id, label: s.name, color: s.color, hint: hints[s.kind] }))}
      onChange={(id) => id && id !== value && onChange(id)}
      trigger={trigger}
    />
  );
}

export function EpicPicker({ value, onChange, trigger }: { value: string; onChange: (id: string) => void; trigger: Parameters<typeof ChoicePopover>[0]["trigger"] }) {
  const { pid, canEdit } = useProject();
  const epics = useEpics(pid);
  return (
    <ChoicePopover
      value={value}
      disabled={!canEdit}
      badge="plain"
      title="Move to epic"
      choices={(epics.data ?? []).map((e) => ({ id: e.id, label: e.title }))}
      onChange={(id) => id && id !== value && onChange(id)}
      trigger={trigger}
    />
  );
}

export function SprintPicker({
  epicId,
  value,
  onChange,
  trigger,
}: {
  epicId: string;
  value: string | null;
  onChange: (id: string | null) => void;
  trigger: Parameters<typeof ChoicePopover>[0]["trigger"];
}) {
  const { pid, canEdit, sprintStatusById } = useProject();
  const sprints = useSprints(pid, epicId);
  return (
    <ChoicePopover
      value={value}
      disabled={!canEdit}
      badge="plain"
      title="Sprints in this epic"
      choices={[
        { id: null, label: "Backlog (no sprint)" },
        ...(sprints.data ?? []).map((s) => ({ id: s.id, label: s.title, hint: s.statusId ? sprintStatusById.get(s.statusId)?.name : undefined })),
      ]}
      onChange={(id) => id !== value && onChange(id)}
      trigger={trigger}
    />
  );
}
